import React, { useEffect, useState } from "react";
import { Row, Col } from "reactstrap";
import withStyles from "isomorphic-style-loader/withStyles";
import * as yup from "yup";
import s from "./styles.scss";
import WizardFormStep from "../../components/WizardFormStep";
import NavigationCard from "./components/NavigationCard";
import SiteActionButtons from "./components/SiteActionButtons";
import GeneralSettingsPanel from "./components/GeneralSettingsPanel";
import { useNavigate } from "react-router-dom";
import { serviceCaller, ServiceCallerProps } from "../../../../../composition";
import { connect } from "react-redux";
import { firebaseConnect, isLoaded } from "react-redux-firebase";
import { get } from "lodash";
import { saveBrandSetupData } from "../../../../../../utils/firebase/brandSetup";
import { ensureCustomizationData } from "../../../../../../utils/firebase/customization";
import {
  PaymentSetupData,
  usesExternalGivingPage,
} from "../../../../../../utils/firebase/paymentSetup";
import { useBrandSetupWizardContext } from "../context";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGears } from "@fortawesome/free-solid-svg-icons";
import newsfeedPageLayout from "../../../../../../media/img/page-layout.png";
import givingPageLayout from "../../../../../../media/img/giving-page-layout.png";
import postDetailsLayout from "../../../../../../media/img/post-details-layout.png";

export const editGivingSiteSchema = yup.object({});

const OTHER_SETTINGS_TITLE = "Other Settings";
const OTHER_SETTINGS_SUBTITLE =
  "Change the highlight color, privacy policy URL and other important details";

type HubView = "cards" | "generalSettings";

interface EditGivingSiteProps extends ServiceCallerProps {
  charityId: string;
  brandSetupData?: any;
  whitelabelUrl?: string | null;
  configHighlightColor?: string | null;
  customDonationUrl?: string | null;
  charityPrivacyPolicyUrl?: string | null;
  charityTermsAndConditionsUrl?: string | null;
  usesExternalGivingPage?: boolean;
  onExit?: () => void;
  returnToCustomizeHub?: boolean;
  publishDisabled?: boolean;
}

const EditGivingSite: React.FC<EditGivingSiteProps> = ({
  charityId,
  brandSetupData,
  whitelabelUrl,
  configHighlightColor,
  customDonationUrl,
  charityPrivacyPolicyUrl,
  charityTermsAndConditionsUrl,
  usesExternalGivingPage: usesExternalGivingPageProp = false,
  currentCharity,
  config,
  serviceAccessToken,
  returnToCustomizeHub = false,
  publishDisabled = false,
}) => {
  const navigate = useNavigate();
  const { showSnackbar, setCustomizeBackOverride, onExit } =
    useBrandSetupWizardContext();
  const [hasVisitedStep, setHasVisitedStep] = useState(false);
  const [activeView, setActiveView] = useState<HubView>("cards");

  const isNewsfeedCompleted = brandSetupData?.newsfeedCustomized || false;
  const isGivingCompleted = brandSetupData?.givingCustomized || false;
  const isPostDetailsCompleted = brandSetupData?.postDetailsCustomized || false;
  const isGeneralSettingsCompleted = Boolean(
    charityPrivacyPolicyUrl?.trim() && charityTermsAndConditionsUrl?.trim()
  );

  useEffect(() => {
    if (activeView !== "generalSettings") {
      setCustomizeBackOverride(null);
      return;
    }

    setCustomizeBackOverride({
      label: "Back to customization",
      onBack: () => setActiveView("cards"),
    });

    return () => setCustomizeBackOverride(null);
  }, [activeView, setCustomizeBackOverride]);

  useEffect(() => {
    if (!charityId || hasVisitedStep) {
      return;
    }

    const alreadyInCustomizationFlow =
      brandSetupData?.progressTracker === "customization_started" ||
      brandSetupData?.progressTracker === "customization_completed" ||
      brandSetupData?.progressTracker === "published";

    setHasVisitedStep(true);

    if (alreadyInCustomizationFlow) {
      return;
    }

    saveBrandSetupData(charityId, {
      progressTracker: "customization_started",
    })
      .then(() => ensureCustomizationData(charityId))
      .catch(console.error);
  }, [charityId, hasVisitedStep, brandSetupData?.progressTracker]);

  const customizerSearch = returnToCustomizeHub ? "&from=hub" : "";

  const handleNewsfeedPageClick = () => {
    navigate(`/onboarding/customation?page=newsfeed${customizerSearch}`);
  };

  const handleDonationPageClick = () => {
    navigate(`/onboarding/customation?page=giving${customizerSearch}`);
  };

  const handlePostDetailsClick = () => {
    navigate(`/onboarding/customation?page=postdetail${customizerSearch}`);
  };

  const handleGeneralSettingsClick = () => {
    setActiveView("generalSettings");
  };

  const handlePublishSuccess = () => {
    setTimeout(() => {
      onExit?.();
    }, 3000);
  };

  if (activeView === "generalSettings") {
    return (
      <WizardFormStep
        title={OTHER_SETTINGS_TITLE}
        subtitle={OTHER_SETTINGS_SUBTITLE}
      >
        <GeneralSettingsPanel
          charityId={currentCharity?.id || charityId}
          config={config}
          serviceAccessToken={serviceAccessToken}
          configHighlightColor={configHighlightColor}
          customDonationUrl={customDonationUrl}
          showExternalDonationUrl={usesExternalGivingPageProp}
          showSnackbar={showSnackbar}
        />
      </WizardFormStep>
    );
  }

  return (
    <WizardFormStep
      title="Customize Your Website"
      subtitle={
        usesExternalGivingPageProp
          ? "Customize your newsfeed branding, messaging, and colors."
          : "Customize your newsfeed and donation page branding, messaging, and colors."
      }
    >
      <SiteActionButtons
        charityId={currentCharity?.id || charityId}
        config={config}
        serviceAccessToken={serviceAccessToken}
        whitelabelUrl={whitelabelUrl}
        showSnackbar={showSnackbar}
        onPublishSuccess={handlePublishSuccess}
        publishDisabled={publishDisabled}
      />

      <Row className={`justify-content-center ${s.cardsRow}`}>
        <Col xs="auto" className="mb-2">
          <NavigationCard
            title="Newsfeed Page"
            description="Customize the navbar, body, and footer on your newsfeed."
            image={newsfeedPageLayout}
            imageAlt="Newsfeed page layout"
            onClick={handleNewsfeedPageClick}
            isCompleted={isNewsfeedCompleted}
          />
        </Col>
        {!usesExternalGivingPageProp && (
          <Col xs="auto" className="mb-2">
            <NavigationCard
              title="Donation Page"
              description="Upload a banner image and customize the headline and donation prompt."
              image={givingPageLayout}
              imageAlt="Donation page layout"
              onClick={handleDonationPageClick}
              isCompleted={isGivingCompleted}
            />
          </Col>
        )}
        <Col xs="auto" className="mb-2">
          <NavigationCard
            title="Post Details"
            description="Customize the post details body"
            image={postDetailsLayout}
            imageAlt="Post details page layout"
            onClick={handlePostDetailsClick}
            isCompleted={isPostDetailsCompleted}
          />
        </Col>
        <Col xs="auto" className="mb-2">
          <NavigationCard
            title="Other Settings"
            description="Change the highlight color, privacy policy URL and other important details"
            icon={
              <FontAwesomeIcon
                icon={faGears}
                style={{ fontSize: 100, width: 100, height: 100, color: "#94a3b8" }}
              />
            }
            onClick={handleGeneralSettingsClick}
            isCompleted={isGeneralSettingsCompleted}
          />
        </Col>
      </Row>
    </WizardFormStep>
  );
};

const mapStateToProps = (state: any) => {
  const { firebase, user } = state;
  const charityId = user.currentCharity?.id || "";

  const dataLoaded =
    charityId &&
    isLoaded(firebase.data[charityId]) &&
    isLoaded(get(firebase, `data.${charityId}.charitySetup.brandSetup`));

  const brandSetupData = dataLoaded
    ? get(firebase, `data.${charityId}.charitySetup.brandSetup`, {})
    : {};

  const paymentSetupLoaded =
    charityId &&
    isLoaded(firebase.data[charityId]) &&
    isLoaded(get(firebase, `data.${charityId}.charitySetup.payment`));

  const paymentSetupData: PaymentSetupData | null = paymentSetupLoaded
    ? get(firebase, `data.${charityId}.charitySetup.payment`, null)
    : null;

  const whitelabelUrl = get(
    firebase,
    `data.${charityId}.config.whitelabelUrl`,
    null
  );

  const configHighlightColor = get(
    firebase,
    `data.${charityId}.config.highlightColor`,
    null
  );

  const customDonationUrl = get(
    firebase,
    `data.${charityId}.config.customDonationUrl`,
    null
  );

  const charityInfo = get(firebase, `data.${charityId}.info`, {});

  return {
    charityId,
    brandSetupData,
    whitelabelUrl,
    configHighlightColor,
    customDonationUrl,
    charityPrivacyPolicyUrl: charityInfo?.privacyPolicyUrl ?? null,
    charityTermsAndConditionsUrl: charityInfo?.termsAndConditionsUrl ?? null,
    usesExternalGivingPage: usesExternalGivingPage(paymentSetupData, customDonationUrl),
  };
};

export default serviceCaller(
  connect(mapStateToProps)(
    firebaseConnect((_, store) => {
      const charityId = store.getState().user.currentCharity?.id;
      return charityId
        ? [
            `${charityId}/charitySetup/brandSetup`,
            `${charityId}/charitySetup/payment`,
            `${charityId}/config/whitelabelUrl`,
            `${charityId}/config/highlightColor`,
            `${charityId}/config/customDonationUrl`,
            `${charityId}/info`,
          ]
        : [];
    })(withStyles(s)(EditGivingSite))
  )
);
